import Link from "next/link"
import { SectionRule } from "./section-rule"

export interface RelatedToolItem {
  title: string
  description: string
  category: string
  href: string
  /** Optional short duration label, e.g. "~30 sec" */
  time?: string
}

interface RelatedToolsRailProps {
  /** The related tools to render — typically 4 */
  tools: RelatedToolItem[]
  /** Section eyebrow (default: "Keep going") */
  eyebrow?: string
  /** Section title (default: "Tools that pair with this one") */
  title?: string
  /** Optional meta label on the right of the header. Omit for a cleaner header. */
  meta?: string
  /** Additional classes on the outer section */
  className?: string
}

/**
 * RelatedToolsRail — 4-up grid of editorial tool cards that sits near the
 * bottom of every tool-page variant. Each card links to another tool and
 * mirrors the card treatment from the /tools index (category label, № NN
 * mono index, orange hover lift).
 *
 * Renders a `SectionRule` header above the grid with a configurable
 * eyebrow/title/meta. Pass any href — external or `next/link`-safe path.
 */
export function RelatedToolsRail({
  tools,
  eyebrow = "Keep going",
  title = "Tools that pair with this one",
  meta,
  className = "",
}: RelatedToolsRailProps) {
  return (
    <section className={`bg-white py-8 md:py-12 ${className}`}>
      <div className="max-w-[1120px] mx-auto px-6 md:px-8">
        <SectionRule eyebrow={eyebrow} title={title} right={meta} />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {tools.map((t, i) => (
            <Link
              key={t.href}
              href={t.href}
              className="group flex flex-col p-5 rounded-xl border border-gray-200 bg-white hover:border-[#ff9951] hover:-translate-y-0.5 transition-all min-h-[180px]"
            >
              <div className="flex items-start justify-between mb-3">
                <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#0469f0]">
                  {t.category}
                </span>
                <span className="font-mono text-[11px] text-gray-500">
                  № {String(i + 1).padStart(2, "0")}
                </span>
              </div>
              <div className="text-[16px] font-extrabold tracking-[-0.015em] text-gray-900 leading-[1.2] mb-1.5">
                {t.title}
              </div>
              <div className="text-[13px] text-gray-500 leading-[1.5]">{t.description}</div>

              {/* Footer pinned to the bottom so divider lines up across all cards regardless of description length */}
              <div className="mt-auto pt-4 flex justify-between items-center border-t border-gray-100">
                <span className="text-[11px] text-gray-500">{t.time ? `⏱ ${t.time}` : ""}</span>
                <span className="text-[12px] font-bold text-[#ff9951]">Open ↗</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  )
}
